import {observer, useLocalStore} from 'mobx-react';
import React from 'react';

import {DiscoveryUnit, DiscoveryUnitItem} from '@udemy/discovery-api';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {Tabs} from '@udemy/react-structure-components';

import {CardContainer} from './card-container.react-component';
import {ImageBanner} from './image-banner.react-component';
import {WindowShoppingUnitStore} from './window-shopping-unit.mobx-store';
import styles from './window-shopping-unit.module.less';

export interface WindowShoppingUnitProps {
    unit: DiscoveryUnit<DiscoveryUnitItem>;
    ariaLabelledBy?: string;
    renderCta?: React.ReactNode;
    showBannerImage?: boolean;
    showNumAdditionalCourses?: boolean;
    cardProps?: Record<string, unknown>;
}

export const WindowShoppingUnit = observer(
    ({
        unit,
        ariaLabelledBy = 'exploreCollectionHeading',
        renderCta,
        showBannerImage = true,
        showNumAdditionalCourses = false,
        cardProps,
    }: WindowShoppingUnitProps) => {
        const {gettext, interpolate} = useI18n();
        const isMobileMax = useMatchMedia('mobile-max');
        const store = useLocalStore(() => new WindowShoppingUnitStore(unit));

        const renderContentWithCTA = (subUnit: (typeof store.subUnits)[number]) => (
            <div className={styles['content-with-cta']}>
                <CardContainer
                    unit={{...subUnit, items: subUnit.items ?? []}}
                    className={styles['card-container']}
                    cardProps={cardProps}
                />
                {showNumAdditionalCourses && (
                    <div
                        className={`ud-heading-sm ${styles['num-courses-text']}`}
                        data-purpose="num-courses-text"
                    >
                        {interpolate(
                            gettext('+%(courseCount)s other top %(topic)s courses'),
                            {courseCount: 0, topic: subUnit.title},
                            true,
                        )}
                    </div>
                )}
                {renderCta && (
                    <div className={styles['cta-wrapper']} data-purpose="cta-wrapper">
                        <div className="ud-heading-lg">
                            {gettext('Start exploring the collection today.')}
                        </div>
                        {renderCta}
                    </div>
                )}
            </div>
        );

        return (
            <section aria-labelledby={ariaLabelledBy}>
                <Tabs prioritizeTouch={!!isMobileMax}>
                    {store.subUnits.map((subUnit) => (
                        <Tabs.Tab title={subUnit.title} key={subUnit.title}>
                            {showBannerImage && subUnit.source_object_id ? (
                                <div className={styles.container}>
                                    <ImageBanner
                                        data-purpose="image-banner"
                                        sourceObjectId={subUnit.source_object_id}
                                    />
                                    {renderContentWithCTA(subUnit)}
                                </div>
                            ) : (
                                renderContentWithCTA(subUnit)
                            )}
                        </Tabs.Tab>
                    ))}
                </Tabs>
            </section>
        );
    },
);
